import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { z } from 'zod';
import { apiGet } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const IslandSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  cdcMode: z.enum(['TRIGGER_JOURNAL', 'NATIVE_CT']),
  /** Tryb wdrożenia wyspy — w Smart Syncu master stoi u nas, nie na maszynie. */
  tryb: z.enum(['ZWYKLY', 'SMART_SYNC']).optional(),
  /**
   * CZY KANAŁ WYDAŃ RODZEŃSTWA WŁAŚNIE NADRABIA — `null` poza Smart Syncem.
   *
   * ⚠️ `nullish`, nie `nullable`: instancja starsza od tej zmiany tego pola nie odda, a schemat
   * ostrzejszy od odpowiedzi zamienia UDANY odczyt w błąd — tu kosztowałby przełącznik wysp,
   * czyli cały portal.
   */
  synchronizacja: z.object({ dociaganie: z.number(), zaleglosc: z.number() }).nullish(),
  /**
   * PRACOWNIKÓW WYSPY PROWADZI HR+ (06.10.2026) — ekran pracowników chowa wtedy dodawanie i mówi,
   * że ręczne zmiany danych z HR+ zostaną nadpisane. `optional` z tego samego powodu co wyżej.
   */
  hrPlus: z.boolean().optional(),
});

const IslandsSchema = z.object({
  clients: z.array(
    z.object({ id: z.string(), code: z.string(), name: z.string(), groups: z.array(IslandSchema) }),
  ),
});

export type Island = z.infer<typeof IslandSchema> & { clientName: string };

const fetchIslands = () => apiGet('/islands', IslandsSchema);

const STORAGE_KEY = 'vendi.groupId';

type IslandContextValue = {
  islands: Island[];
  groupId: string | null;
  island: Island | null;
  setGroupId: (id: string) => void;
  isLoading: boolean;
};

const IslandContext = createContext<IslandContextValue | undefined>(undefined);

/**
 * Aktywna wyspa (klient/grupa) trzymana po stronie FRONTU i przekazywana jawnie
 * jako `groupId` w każdym żądaniu modułu domenowego.
 *
 * Świadome odejście od oryginalnego Vendi, które trzymało kontekst w ciasteczku
 * `sps_ctx`: ciasteczko jest wspólne dla wszystkich kart przeglądarki, więc otwarcie
 * dwóch wysp obok siebie przestawiało jedną z nich pod użytkownikiem. Jawny parametr
 * jest też bezstanowy po stronie serwera (12-factor) i cache'owalny.
 */
export function IslandProvider({ children }: { children: ReactNode }) {
  /**
   * ⚠️⚠️ DOPÓKI NIE MA SESJI, NIE PYTAMY O WYSPY — i to jest poprawka BŁĘDU ZOBACZONEGO U KLIENTA
   * (02.09.2026): „gdy osoba loguje się na instancję, na której jeszcze nigdy nie była na tym
   * komputerze, to dopóki nie kliknie ctrl+shift+R, ma wszystko puste, mimo że instancja jest
   * zasiedlona danymi". Szef zalogował się u siebie i zgłosił, że instancja nie działa.
   *
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  MECHANIZM, KTÓRY TO ROBIŁ. `IslandProvider` stoi WEWNĄTRZ `AuthProvider`, więc montuje się
   *  razem z nim — a to zapytanie leciało natychmiast, jeszcze przed rozstrzygnięciem sesji.
   *  Bez ciasteczka wracało `401`, `retry: 1` powtarzał je równie wcześnie, a `staleTime`
   *  pięciu minut trzymał ten BŁĄD w pamięci zapytań. Lista wysp zostawała więc pusta, a skoro
   *  pusta, to `groupId` nigdy się nie ustawiał — i każdy ekran pokazywał „brak wyspy".
   *
   *  ⚠️ DLACZEGO POMAGAŁO ODŚWIEŻENIE, a nie ponowne logowanie: przeładowanie strony tworzy nowy
   *  `QueryClient`, więc zapamiętany błąd znika i zapytanie leci już z sesją. Bez przeładowania
   *  nic tego nie unieważniało — dlatego wyglądało to jak „instancja nie działa".
   *
   *  ⚠️ TO NIE BYŁ CACHE PRZEGLĄDARKI, choć `ctrl+shift+R` sugerował właśnie to. Twardy przeładunek
   *  pomagał TYM SAMYM co zwykły: nowym `QueryClient`em.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const { user, isLoading: sesjaWToku } = useAuth();
  const { data, isPending } = useQuery({
    queryKey: ['islands'],
    queryFn: fetchIslands,
    staleTime: 300_000,
    /**
     * ⚠️ ODŚWIEŻANIE CO MINUTĘ — dla plakietki „synchronizacja w toku". Bez niego stan wisiałby
     * do przeładowania strony, a dociąganie historii trwa godzinami i ma się SAMO zgasić.
     * Minuta, bo to cykl agenta: częściej nie ma czego zobaczyć.
     */
    refetchInterval: 60_000,
    enabled: user !== null,
  });

  const islands = useMemo<Island[]>(
    () =>
      (data?.clients ?? []).flatMap((c) => c.groups.map((g) => ({ ...g, clientName: c.name }))),
    [data],
  );

  const [groupId, setGroupIdState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  // pierwsza wyspa jako domyślna; czyścimy zapamiętane id, jeśli wyspa zniknęła
  useEffect(() => {
    if (islands.length === 0) return;
    const known = groupId !== null && islands.some((i) => i.id === groupId);
    if (!known) {
      const first = islands[0]!.id;
      setGroupIdState(first);
      localStorage.setItem(STORAGE_KEY, first);
    }
  }, [islands, groupId]);

  const setGroupId = useCallback((id: string) => {
    setGroupIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const value = useMemo<IslandContextValue>(
    () => ({
      islands,
      groupId,
      island: islands.find((i) => i.id === groupId) ?? null,
      setGroupId,
      /**
       * ⚠️ CZEKANIE NA SESJĘ TO TEŻ WCZYTYWANIE. Zapytanie wyłączone ma w react-query stan
       * `pending`, ale bez tej sumy ekran przez chwilę po zalogowaniu mówiłby „brak wyspy"
       * zamiast „wczytuję" — czyli dokładnie tym zdaniem, które zgłoszono jako awarię.
       */
      isLoading: sesjaWToku || isPending,
    }),
    [islands, groupId, setGroupId, sesjaWToku, isPending],
  );

  return <IslandContext.Provider value={value}>{children}</IslandContext.Provider>;
}

export function useIslands(): IslandContextValue {
  const ctx = useContext(IslandContext);
  if (!ctx) throw new Error('useIslands musi być wewnątrz IslandProvider');
  return ctx;
}
